import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./CompaniesPage.scss";
import api from "../api/axios";

export default function CompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState("");
    const [country, setCountry] = useState("all");
    const [region, setRegion] = useState("all");
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ by: "lastActive", dir: "desc" }); // default: по активности, новые сверху

    const navigate = useNavigate();

    // Для фильтрации
    const countryOptions = useMemo(() => {
        const set = new Set();
        companies.forEach(c => c.country && set.add(c.country));
        return Array.from(set);
    }, [companies]);
    const regionOptions = useMemo(() => {
        const set = new Set();
        companies
            .filter(c => country === "all" || c.country === country)
            .forEach(c => c.region && set.add(c.region));
        return Array.from(set);
    }, [companies, country]);

    // Загрузка данных
    useEffect(() => {
        setLoading(true);
        api.get("/admin/companies")
            .then(resp => setCompanies(resp.data))
            .catch(() => setCompanies([]))
            .finally(() => setLoading(false));
    }, []);

    // Фильтрация и поиск
    const filtered = useMemo(() => {
        return companies.filter(c =>
            (country === "all" || c.country === country) &&
            (region === "all" || c.region === region) &&
            (search.trim() === "" || (
                c.title?.toLowerCase().includes(search.toLowerCase()) ||
                c.city?.toLowerCase().includes(search.toLowerCase()) ||
                c.address?.toLowerCase().includes(search.toLowerCase()) ||
                c.inn?.toLowerCase().includes(search.toLowerCase()) ||
                c.manager?.toLowerCase().includes(search.toLowerCase()) ||
                c.comment?.toLowerCase().includes(search.toLowerCase()) ||
                (Array.isArray(c.deviceCodes) && c.deviceCodes.some(code => (code + '').includes(search)))
            ))
        );
    }, [companies, search, country, region]);

    // Сортировка
    const sorted = useMemo(() => {
        const arr = [...filtered];
        if (sort.by === "activePlaylistTitle") {
            arr.sort((a, b) => {
                const valA = (a.activePlaylistTitle || "").toLowerCase();
                const valB = (b.activePlaylistTitle || "").toLowerCase();
                if (valA === valB) return 0;
                if (sort.dir === "asc") return valA > valB ? 1 : -1;
                return valA < valB ? 1 : -1;
            });
        } else if (sort.by === "lastActive") {
            arr.sort((a, b) => {
                const dateA = a.lastActive ? new Date(a.lastActive) : 0;
                const dateB = b.lastActive ? new Date(b.lastActive) : 0;
                if (dateA === dateB) return 0;
                if (sort.dir === "asc") return dateA - dateB;
                return dateB - dateA;
            });
        }
        return arr;
    }, [filtered, sort]);

    // Переключатель сортировки
    const toggleSort = (by) => {
        setSort(prev => {
            if (prev.by === by) {
                // Переключение направления
                return { by, dir: prev.dir === "asc" ? "desc" : "asc" };
            }
            return { by, dir: "asc" };
        });
    };

    // Навигация на детальную
    const handleRowClick = (id) => {
        navigate(`/companies/${id}`);
    };

    // Для визуализации стрелки
    const renderSortArrow = (column) => {
        if (sort.by !== column) return null;
        return sort.dir === "asc" ? " ▲" : " ▼";
    };

    return (
        <div className="companies-page">
            <div className="companies-header">
                <h2>Компании</h2>
                <div className="companies-actions">
                    <input
                        type="text"
                        className="companies-search"
                        placeholder="Поиск по названию, адресу, ИП, менеджеру, коду приставки..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <select
                        className="companies-filter"
                        value={country}
                        onChange={e => setCountry(e.target.value)}
                    >
                        <option value="all">Страна: Все</option>
                        {countryOptions.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <select
                        className="companies-filter"
                        value={region}
                        onChange={e => setRegion(e.target.value)}
                        disabled={country === "all"}
                    >
                        <option value="all">Регион: Все</option>
                        {regionOptions.map(r => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="companies-table-wrap">
                {loading ? (
                    <div className="companies-loading">Загрузка...</div>
                ) : (
                    <table className="companies-table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Страна</th>
                            <th>Регион</th>
                            <th>Город</th>
                            <th>Адрес</th>
                            <th
                                className={sort.by === "activePlaylistTitle" ? "sortable active" : "sortable"}
                                style={{ cursor: "pointer", userSelect: "none" }}
                                onClick={() => toggleSort("activePlaylistTitle")}
                            >
                                Плейлист{renderSortArrow("activePlaylistTitle")}
                            </th>
	                            <th
	                                className={sort.by === "lastActive" ? "sortable active" : "sortable"}
	                                style={{ cursor: "pointer", userSelect: "none" }}
	                                onClick={() => toggleSort("lastActive")}
	                            >
	                                Активность{renderSortArrow("lastActive")}
	                            </th>
	                            <th>Уведомления</th>
	                            <th>ИП</th>
	                            <th>Менеджер</th>
	                            <th>Комментарий</th>
	                            <th>Код приставки</th>
	                        </tr>
	                        </thead>
	                        <tbody>
	                        {sorted.length === 0 ? (
	                            <tr>
	                                <td colSpan={12} className="companies-empty">
	                                    Нет данных по заданным фильтрам
	                                </td>
	                            </tr>
	                        ) : (
	                            sorted.map(c => (
	                                <tr
                                    key={c.id}
                                    className="companies-table-row"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleRowClick(c.id)}
                                >
                                    <td>{c.id}</td>
	                                    <td>{c.country || "-"}</td>
	                                    <td>{c.region || "-"}</td>
	                                    <td>{c.city || "-"}</td>
	                                    <td>{c.address || "-"}</td>
	                                    <td>{c.activePlaylistTitle || "-"}</td>
	                                    <td>{c.lastActive ? new Date(c.lastActive).toLocaleString() : "-"}</td>
	                                    <td>
	                                        <span
	                                            className={
	                                                c.isInactiveNotificationDisabled
	                                                    ? "companies-notification-status disabled"
	                                                    : "companies-notification-status enabled"
	                                            }
	                                        >
	                                            {c.isInactiveNotificationDisabled ? "Отключены" : "Включены"}
	                                        </span>
	                                    </td>
	                                    <td>{c.legalName || "-"}</td>
	                                    <td>{c.manager || "-"}</td>
	                                    <td>{c.comment || ""}</td>
                                    <td>
                                        {Array.isArray(c.deviceCodes)
                                            ? c.deviceCodes.join(", ")
                                            : (c.deviceCodes || "-")
                                        }
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
